// Styles for the SearchFormSkeletonLoader.
//
// The skeleton stands in for the real SearchForm while the AB-test target call
// is in flight, so its box (height + width + top offset) is driven by
// per-breakpoint values that mirror the real components:
//   - Height: matches the baseline SearchForm mobile/desktop wrapper.
//   - Width : matches the Next-Gen SearchForm (Bootstrap `.container` widths).
//   - Top   : mirrors `StickyComponentWrapper` via `getNonStickyTop`.
import styled from 'styled-components';
import rtl from 'styled-components-rtl';
import { baseVariables, toRem } from '@marriott/mi-ui-library';

export type SkeletonLoaderBreakpoint = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Rendered height of the baseline SearchForm pill per breakpoint.
 * xs/sm mirror `StyledSearchFormMobileWrapperDiv` (40px mobile pill + padding),
 * md/lg mirror the desktop `.search-form-wrapper` primary-wrapper height.
 */
export const BASELINE_SEARCH_FORM_HEIGHTS: Record<SkeletonLoaderBreakpoint, number> = {
  xs: 76,
  sm: 76,
  md: 100,
  lg: 100,
};

/**
 * Max width of the Next-Gen SearchForm container per breakpoint.
 * Mirrors Bootstrap `.container` used by `NGSearchForm` (`container px-0`).
 * `xs` is a full-bleed container with 16px horizontal gutters (mobile pill),
 * sm/md/lg align to Bootstrap's responsive `.container` max-widths, capped at
 * the Marriott 1140px design grid for desktop.
 */
export const NGSF_CONTAINER_MAX_WIDTHS: Record<SkeletonLoaderBreakpoint, number | 'none'> = {
  xs: 'none',
  sm: 540,
  md: 720,
  lg: 1140,
};

/**
 * Horizontal gutter applied to the skeleton container per breakpoint.
 * Matches the outer gutters used by the mobile SearchForm containers.
 */
export const NGSF_CONTAINER_GUTTERS: Record<SkeletonLoaderBreakpoint, number> = {
  xs: 16,
  sm: 8,
  md: 0,
  lg: 0,
};

interface SkeletonLoaderFormContainerProps {
  /** Per-breakpoint top offset (mirrors `StickyComponentWrapper.getNonStickyTop`). */
  $topOffsetXs: string;
  $topOffsetSm: string;
  $topOffsetMd: string;
  $topOffsetLg: string;
  /** Per-breakpoint skeleton height (baseline SearchForm). */
  $heightXs: string;
  $heightSm: string;
  $heightMd: string;
  $heightLg: string;
  /** Per-breakpoint skeleton max-width (Next-Gen SearchForm container). */
  $maxWidthXs: string;
  $maxWidthSm: string;
  $maxWidthMd: string;
  $maxWidthLg: string;
  /** Per-breakpoint horizontal gutter for the container. */
  $gutterXs: string;
  $gutterSm: string;
  $gutterMd: string;
  $gutterLg: string;
}

export const StyledSkeletonLoaderFormContainer = styled.div<SkeletonLoaderFormContainerProps>`
  ${rtl`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    /* Mobile (xs): no top padding so the skeleton starts exactly where the SearchForm pill lands. */
    padding-top: 0;
    padding-bottom: 0;

    &.search-form-position {
      position: absolute;
      width: 100%;
      left: 0;
      right: 0;
      /* Match baseline SearchForm overlay positioning on mobile: use the computed top offset directly. */
      top: ${({ $topOffsetXs }: SkeletonLoaderFormContainerProps) => $topOffsetXs};
      padding-top: ${toRem(24)};
      padding-bottom: 0;
      z-index: 49;

      @media ${baseVariables.mediaQuery.sm} {
        top: ${({ $topOffsetSm }: SkeletonLoaderFormContainerProps) => $topOffsetSm};
        left: 0;
        right: 0;
      }

      @media ${baseVariables.mediaQuery.md} {
        top: ${({ $topOffsetMd }: SkeletonLoaderFormContainerProps) => $topOffsetMd};
        left: 0;
        right: 0;
      }

      @media ${baseVariables.mediaQuery.lg} {
        top: ${({ $topOffsetLg }: SkeletonLoaderFormContainerProps) => $topOffsetLg};
        z-index: 49;
      }
    }

    .search-form-wrapper {
      width: 100%;
      /* NGSF widths: xs is full-bleed with mobile gutters, sm/md/lg snap to
         the Bootstrap .container max-widths (see NGSF_CONTAINER_MAX_WIDTHS). */
      max-width: ${({ $maxWidthXs }: SkeletonLoaderFormContainerProps) => $maxWidthXs};
      margin: 0 auto;
      padding: 0 ${({ $gutterXs }: SkeletonLoaderFormContainerProps) => $gutterXs};
      box-sizing: border-box;

      @media ${baseVariables.mediaQuery.sm} {
        max-width: ${({ $maxWidthSm }: SkeletonLoaderFormContainerProps) => $maxWidthSm};
        padding: 0 ${({ $gutterSm }: SkeletonLoaderFormContainerProps) => $gutterSm};
      }

      @media ${baseVariables.mediaQuery.md} {
        max-width: ${({ $maxWidthMd }: SkeletonLoaderFormContainerProps) => $maxWidthMd};
        padding: 0 ${({ $gutterMd }: SkeletonLoaderFormContainerProps) => $gutterMd};
      }

      @media ${baseVariables.mediaQuery.lg} {
        max-width: ${({ $maxWidthLg }: SkeletonLoaderFormContainerProps) => $maxWidthLg};
        padding: 0 ${({ $gutterLg }: SkeletonLoaderFormContainerProps) => $gutterLg};
      }

      .search-form-skeleton {
        width: 100%;
        /* Baseline SearchForm heights per viewport (see BASELINE_SEARCH_FORM_HEIGHTS). */
        height: ${({ $heightXs }: SkeletonLoaderFormContainerProps) => $heightXs};
        border-radius: ${toRem(8)};
        background-color: ${baseVariables.color['light-gray-1']};
        overflow: hidden;
        position: relative;
        box-shadow: 0 ${toRem(8)} ${toRem(20)} 0 rgba(0, 0, 0, 0.1);

        @media ${baseVariables.mediaQuery.sm} {
          height: ${({ $heightSm }: SkeletonLoaderFormContainerProps) => $heightSm};
        }

        @media ${baseVariables.mediaQuery.md} {
          height: ${({ $heightMd }: SkeletonLoaderFormContainerProps) => $heightMd};
          border-radius: ${toRem(12)};
        }

        @media ${baseVariables.mediaQuery.lg} {
          height: ${({ $heightLg }: SkeletonLoaderFormContainerProps) => $heightLg};
          border-radius: ${toRem(16)};
          box-shadow: 0 ${toRem(4)} ${toRem(20)} 0 rgba(0, 0, 0, 0.07);
        }

        .skeleton-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.3;
          -webkit-animation: fadein 2s ease-in alternate infinite;
          -moz-animation: fadein 2s ease-in alternate infinite;
          animation: fadein 2s ease-in alternate infinite;
        }
      }
    }

    @-webkit-keyframes fadein {
      10%   { opacity: 0.3; }
      25%   { opacity: 0.35; }
      50%   { opacity: 0.4; }
      75%   { opacity: 0.5; }
      100% { opacity: 0.6; }
    }
    @-moz-keyframes fadein {
      10%   { opacity: 0.3; }
      25%   { opacity: 0.35; }
      50%   { opacity: 0.4; }
      75%   { opacity: 0.5; }
      100% { opacity: 0.6; }
    }
    @keyframes fadein {
      10%   { opacity: 0.3; }
      25%   { opacity: 0.35; }
      50%   { opacity: 0.4; }
      75%   { opacity: 0.5; }
      100% { opacity: 0.6; }
    }
  `}
`;

export const StyledSkeletonLoaderDiv = styled.div`
  ${rtl`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;
